import { useBulkActionsEntry } from "@/common/hooks/queries/bulkActions/useBulkActionsEntry";
import Modal from "../Modal";
import React, { useEffect, useState } from "react";
import Button from "../Button";
import {
  AlertTriangleIcon,
  ChevronLeftIcon,
  FileSpreadsheetIcon,
  TriangleAlert,
} from "lucide-react";
import Badge from "../Badge";
import { bulkActionsEntryStatus } from "@/common/constants/bulkActions/entryStatus.constant";
import { FileUploader } from "../FileUploader";
import { getFilenameFromUrl } from "@/common/utils/getFilenameFromUrl";
import { useBulkActionsEntryUpdateFile } from "@/common/hooks/queries/bulkActions/useBulkActionsEntryUpdateFile";
import { useQueryClient } from "@tanstack/react-query";
import { useBulkActionsEntryConfirm } from "@/common/hooks/queries/bulkActions/useBulkActionsEntryConfirm";
import { useBulkActionsEntryUpdateTemplate } from "@/common/hooks/queries/bulkActions/useBulkActionsEntryUpdateTemplate";
import { ModalMessageSimple } from "../ModalMessageSimple";
import Spinner from "../Spinner";
import { BulkActionEntryResultData } from "./components/ResultData";
import { BulkActionEntrySuccessModal } from "./components/SuccessModal";
import { bulkActionsEntryModalHelper } from "./constants/modalHelper.constant";

interface BulkActionTemplateModalProps {
  entryId?: string;
  setEntryId: React.Dispatch<React.SetStateAction<string | undefined>>;
  setActionerKlassToOpenNewEntryModal?: React.Dispatch<React.SetStateAction<string | undefined>>;
  onEntryCompleted?: VoidFunction;
}

const BulkActionTemplateModal = ({ entryId, setEntryId, setActionerKlassToOpenNewEntryModal, onEntryCompleted }: BulkActionTemplateModalProps) => {
  const queryClient = useQueryClient();

  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

  const [messages, setMessages] = useState<string[] | boolean>(false);
  const [isError, setIsError] = useState(false);

  const [currentStatus, setCurrentStatus] = useState<string>();

  const { data: entry } = useBulkActionsEntry(entryId, ['generating_template', 'processing', 'completing'].includes(currentStatus || ''));

  const { mutate: updateFile, isPending: isUpdateFilePending } = useBulkActionsEntryUpdateFile({
    entryId,
    onSuccess: () => {
      setIsError(false);
      queryClient.invalidateQueries({ queryKey: ['bulkActionsEntry', entryId] });
    },
    onError: (error) => {
      setIsError(true);
      setMessages(error.response?.data.errors.base || ['Ocorreu um erro ao atualizar arquivo']);
    },
  });

  const { mutate: confirmEntry } = useBulkActionsEntryConfirm({
    entryId,
    onSuccess: () => {
      setIsError(false);
      queryClient.invalidateQueries({ queryKey: ['bulkActionsEntry', entryId] });
    },
    onError: (error) => {
      setIsError(true);
      setMessages(error.response?.data.errors.base || ['Ocorreu um erro ao confirmar ação em massa']);
    },
  });

  const { mutate: updateTemplate, isPending: isUpdateTemplatePending } = useBulkActionsEntryUpdateTemplate({
    entryId,
    onSuccess: () => {
      setIsError(false);
      queryClient.invalidateQueries({ queryKey: ['bulkActionsEntry', entryId] });
    },
    onError: (error: any) => {
      setIsError(true);
      setMessages(error.response?.data.errors.base || ['Ocorreu um erro ao atualizar template']);
    },
  });

  const closeModal = () => {
    setCurrentStatus(undefined);
    setEntryId(undefined);
  };

  useEffect(() => {
    if (entry?.status === 'completed' && currentStatus && currentStatus !== 'completed') {
      setIsSuccessModalOpen(true);
      onEntryCompleted?.();
    }

    setCurrentStatus(entry?.status);
  }, [entry?.status]);

  const displayUploadedFile = ['awaiting_file', 'with_failure', 'awaiting_confirmation', 'completing', 'completed'].includes(entry?.status || '') && entry?.uploaded_file?.url;

  return (
    <>
      <Modal
        isOpen={!!entryId}
        setIsOpen={(isOpen) => {
          if (!isOpen) {
            closeModal();
          }
        }}
        className="min-w-[860px] min-h-[640px] max-h-[640px] bg-black-900 flex flex-col"
        titleClass="h-min"
        title={(
          <div className="flex items-center gap-3 w-full">
            {setActionerKlassToOpenNewEntryModal && (
              <Button
                variant="link-white"
                onClick={() => {
                  setActionerKlassToOpenNewEntryModal(entry?.actioner_klass);
                  closeModal();
                }}
              >
                <ChevronLeftIcon strokeWidth={1.5} />
              </Button>
            )}

            <p>{entry?.title}</p>

            {entry?.status && (
              <Badge
                Icon={entry.status === 'with_failure' ? AlertTriangleIcon : React.Fragment}
                iconProps={{ size: 18 }}
                text={bulkActionsEntryStatus[entry.status as keyof typeof bulkActionsEntryStatus] || entry.status}
                omitBackgroundClass
                className={
                  (entry.status === 'with_failure' ? 'bg-danger'
                    : entry.status === 'completing' ? '!bg-blue-400 !text-black-900'
                      : entry.status === 'completed' || entry.status === 'awaiting_confirmation' ? '!bg-green-600'
                        : 'bg-yellow-500 !text-black-900')
                }
              />
            )}
          </div>
        )}
        body={(
          <div className="text-white-400 flex flex-col gap-4 h-full max-h-[510px] overflow-y-auto">
            {(entry?.status === 'generating_template' || !entry?.status) && (
              <div className="flex flex-col gap-6 items-center justify-center pt-40">
                <div className="flex flex-col gap-1 items-center">
                  <p className="text-lg text-white-800">Gerando arquivo do template</p>
                  <p>Aguarde o arquivo ser gerado para continuar com a ação em massa</p>
                </div>

                <Spinner className="fill-white-400" />
              </div>
            )}

            {entry?.status !== 'generating_template' && (
              <>
                <div className="flex items-center justify-between">
                  <p>{entry?.status === 'completed' && entry?.title}{bulkActionsEntryModalHelper[entry?.status as keyof typeof bulkActionsEntryModalHelper]}</p>

                  <div className="flex gap-2"> 
                      <Button
                        variant="neutral"
                        className="text-white-800"
                        disabled={isUpdateTemplatePending}
                        onClick={() => updateTemplate()}
                      >
                        Atualizar Template
                      </Button>
                  
                    {entry?.status !== 'completed' && (
                      <Button
                        className="text-white-800"
                        disabled={entry?.status !== 'awaiting_confirmation'}
                        onClick={() => confirmEntry()}
                      >
                        Confirmar ação em massa
                      </Button>
                    )}
                  </div>
                </div>

                {!['processing', 'completing', 'completed'].includes(entry?.status || '') && (
                  <div className="flex gap-4">
                    {displayUploadedFile && entry?.uploaded_file?.url ? (
                      <div className="w-1/3 max-w-1/3 overflow-hidden">
                        <FileUploader
                          disabled
                          className="w-full text-center items-center h-[180px]"
                          currentFilePlaceholderClassName="flex flex-col gap-3 items-center"
                          currentFilePlaceholderFileNameClassName="text-md font-light max-h-[40px]"
                          fileName={getFilenameFromUrl(entry?.uploaded_file?.url)}
                          currentFileUrl={entry?.uploaded_file?.url}
                          currentFilePlaceholderIcon={(
                            <div className="flex flex-col items-center gap-2">
                              <p className="text-sm italic font-light text-white-300">Último arquivo enviado</p>

                              <FileSpreadsheetIcon size={48} strokeWidth={1} />
                            </div>
                          )}
                        />
                      </div>
                    ) : entry?.template_file?.url ? (
                      <div className="w-1/3 max-w-1/3 overflow-hidden">
                        <FileUploader
                          disabled
                          className="text-center items-center h-[180px]"
                          currentFilePlaceholderClassName="flex flex-col gap-3 items-center"
                          currentFilePlaceholderFileNameClassName="text-md font-light max-h-[40px]"
                          fileName={entry?.template_file?.url && getFilenameFromUrl(entry?.template_file?.url)}
                          currentFileUrl={entry?.template_file?.url}
                          currentFilePlaceholderIcon={(
                            <div className="flex flex-col items-center gap-2">
                              <p className="text-sm italic font-light text-white-300">Arquivo de template</p>

                              <FileSpreadsheetIcon size={48} strokeWidth={1} />
                            </div>
                          )}
                        />
                      </div>
                    ) : <></>}

                    {entry?.generated_file?.url && (
                      <div className="w-1/3 max-w-1/3 overflow-hidden">
                        <FileUploader
                          disabled
                          className="w-full text-center items-center h-[180px]"
                          currentFilePlaceholderClassName="flex flex-col gap-3 items-center"
                          currentFilePlaceholderFileNameClassName="text-md font-light max-h-[40px]"
                          fileName={getFilenameFromUrl(entry.generated_file.url)}
                          currentFileUrl={entry.generated_file.url}
                          currentFilePlaceholderIcon={(
                            <div className="flex flex-col items-center gap-2">
                              <p className="text-sm italic font-light text-white-300">Arquivo gerado para correção</p>

                              <FileSpreadsheetIcon size={48} strokeWidth={1} />
                            </div>
                          )}
                        />
                      </div>
                    )}

                    <div className={
                      (entry?.template_file?.url || displayUploadedFile)
                        ? entry?.generated_file?.url
                          ? 'w-1/3'
                          : 'w-2/3'
                        : 'w-full'
                    }>
                      <FileUploader
                        containerClassName="h-full h-[180px]"
                        className="h-full w-full bg-yellow-op-2 border-none"
                        currentFilePlaceholderFileNameClassName="max-h-[40px]"
                        title={
                          entry?.uploaded_file?.url
                            ? 'Clique para anexar o arquivo com correções'
                            : 'Clique para anexar o arquivo preenchido'
                        }
                        accept=".xlsx, .csv"
                        handleChange={updateFile}
                        isLoading={isUpdateFilePending}
                        disabled={isUpdateFilePending}
                      />
                    </div>
                  </div>
                )}

                {['processing', 'completing', 'completed'].includes(entry?.status || '') && (
                  <div>
                    <FileUploader
                      disabled
                      className="text-center items-center h-[180px] w-full bg-success-op border-none"
                      currentFilePlaceholderClassName="flex flex-col gap-3 items-center"
                      currentFilePlaceholderFileNameClassName="text-md font-light max-h-[40px]"
                      fileName={entry?.uploaded_file?.url ? getFilenameFromUrl(entry?.uploaded_file?.url) : ''}
                      currentFileUrl={entry?.uploaded_file?.url}
                      currentFilePlaceholderIcon={(
                        <div className={entry?.status === 'completed' ? 'mt-6' : ''}>
                          <FileSpreadsheetIcon size={48} strokeWidth={1} />
                        </div>
                      )}
                      isLoading={entry?.status !== 'completed'}
                    />
                  </div>
                )}
              </>
            )}

            {entry?.status === 'with_failure' && entry?.failure_reason && (
              <div className="flex flex-col gap-2 bg-danger-op rounded p-3">
                <div className="flex items-center gap-2 text-white-800">
                  <TriangleAlert strokeWidth={1.5} />

                  <p className="pt-px">Ocorreu um erro</p>
                </div>

                <p className="text-xs text-white-400">
                  {entry?.failure_reason}
                </p>
              </div>
            )}

            {entry && <BulkActionEntryResultData entry={entry} />}
          </div>
        )}
      />

      {entryId && entry && (
        <BulkActionEntrySuccessModal
          isOpen={isSuccessModalOpen}
          setIsOpen={setIsSuccessModalOpen}
          entry={entry}
          onCloseModal={closeModal}
          setActionerKlassToOpenNewEntryModal={setActionerKlassToOpenNewEntryModal}
        />
      )}

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError={isError}
        />
      )}
    </>
  );
};

export default BulkActionTemplateModal;
